import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EnvironmentService, TranslationService } from '@dch/ng-common';
import { saveDocument } from '../../services/save-document.service';
import { OnDestroyHookDirective } from '../../common/hooks';
import {
  DocType,
  DownloadOptions,
  FileDownloaderService,
} from '../../services/file-downloader.service';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system';

@Component({
  selector: 'my-bill-pdf-document',
  templateUrl: './my-bill-pdf-document.component.html',
  standalone: false,
})
export class MyBillPdfDocumentComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  @Input() documentDate: string;
  @Input() paId: string;
  @Input() documentId: string;
  @Input() customerId?: number;
  @Input() buttonZone?: string = 'mybill';
  @Input() docType?: 'pdf' | 'xml' = 'pdf';
  @Input() showThirdParty: boolean;
  @Input() isLink = false;

  @Output() downloadFileError: EventEmitter<{
    hasCsvError: boolean;
    invoiceError?: boolean;
  }> = new EventEmitter();

  isDownloadInProgress = false;
  hasNewDownloadPdfApiEnabled: boolean;
  hasError = false;
  messageBoxTitle?: string;
  messageBoxContent?: string;
  messageBoxTypeError = MessageBoxTypesEnum.Error;

  linkLabel: string;
  linkSubLabel: string;
  tmsButtonName: string;

  constructor(
    private environmentService: EnvironmentService,
    private translationService: TranslationService,
    private fileDownloaderService: FileDownloaderService
  ) {
    super();
  }

  ngOnInit(): void {
    this.hasNewDownloadPdfApiEnabled =
      this.translationService.getTranslatedValueFor(
        'bills.cover.newDownloadPdfApiEnabled'
      ) === 'true';
    if (this.docType === 'xml' && this.showThirdParty) {
      this.tmsButtonName = 'download third party ubl xml';
      this.linkSubLabel = this.translationService.getTranslatedValueFor(
        'bills.summary.downloadThirdPartyXMLButtonSubLabel'
      );
      this.linkLabel = this.translationService.getTranslatedValueFor(
        'bills.summary.downloadThirdPartyXMLButtonLabel'
      );
    } else if (this.docType === 'xml') {
      this.tmsButtonName = 'download ubl xml';
      this.linkLabel = this.translationService.getTranslatedValueFor(
        'bills.summary.downloadXMLButtonLabel'
      );
      this.linkSubLabel = this.translationService.getTranslatedValueFor(
        'bills.summary.downloadXMLButtonSubLabel'
      );
    } else {
      this.linkLabel = this.translationService.getTranslatedValueFor(
        'bills.summary.downloadButtonLabel'
      );
      this.tmsButtonName = 'download pdf';
      this.linkSubLabel = this.translationService.getTranslatedValueFor(
        'bills.summary.downloadPdfButtonSubLabel'
      );
    }
  }

  onDownloadPdf(docType: DocType = DocType.PDF): void {
    this.setDownloadProgressState(true);
    this.clearErrorState();
    const downloadOptions: DownloadOptions = {
      paId: this.paId,
      documentId: this.documentId,
      docType: docType,
      documentDate: this.documentDate,
      cdbid: this.customerId,
      useV2: this.hasNewDownloadPdfApiEnabled,
    };
    const downloadObservable =
      this.fileDownloaderService.getDownloadedFile(downloadOptions);
    this.addSubscription(
      downloadObservable.subscribe({
        next: (response) => this.handleDownloadSuccess(response, docType),
        error: (err) => this.handleDownloadError(err),
      })
    );
  }

  private setDownloadProgressState(inProgress: boolean): void {
    this.isDownloadInProgress = inProgress;
  }

  private clearErrorState(): void {
    this.hasError = false;
    this.messageBoxTitle = '';
    this.messageBoxContent = '';
    this.downloadFileError.emit({ hasCsvError: false, invoiceError: false });
  }

  private resetDownloadProgress(): void {
    this.isDownloadInProgress = false;
  }

  private handleDownloadSuccess(response: any, docType: DocType): void {
    this.resetDownloadProgress();

    if (response.fileResponse) {
      this.processDownloadedFile(response, docType);
      this.clearErrorState();
    } else {
      this.handleDownloadError(response.error);
    }
  }

  private processDownloadedFile(response: any, docType: DocType): void {
    const fileName = `${this.documentId}.${docType}`;

    if (this.environmentService.isProximusPlusMobileApp) {
      this.downloadForMobileApp(response, fileName, docType);
    } else {
      this.downloadForBrowser(response.fileResponse, fileName, docType);
    }
  }

  private downloadForMobileApp(
    response: any,
    fileName: string,
    docType: string
  ): void {
    const downloadUrl = this.getDownloadUrl(
      response.entityUrl,
      response.version,
      fileName,
      docType
    );
    // @ts-ignore
    window.mypx?.downloadFile?.(downloadUrl);
  }

  private downloadForBrowser(
    fileResponse: ArrayBuffer,
    fileName: string,
    docType: string
  ): void {
    saveDocument(fileResponse, fileName, `application/${docType}`);
  }

  private handleDownloadError(error: any): void {
    this.hasError = true;
    this.resetDownloadProgress();
    this.downloadFileError.emit({ hasCsvError: false, invoiceError: true });
    this.messageBoxTitle = this.translationService.getTranslatedValueFor(
      'bills.pdfScreen.error.downloadTitle'
    );
    this.messageBoxContent = this.translationService.getTranslatedValueFor(
      'bills.pdfScreen.error.download'
    );
  }

  private getDownloadUrl(
    entityUrl: string,
    version: string,
    fileName: string,
    docType: string
  ): string {
    const baseUrl = window.location.origin;

    if (version === 'v1') {
      return `${baseUrl}${entityUrl}#${fileName}`;
    }

    return `${baseUrl}/rest/bill-downloader/customer/${this.customerId}/billingAccount/fls/${this.paId}/billingDocument/${this.documentId}/${docType}#${fileName}`;
  }
}
